
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { KanbanColumn } from './KanbanColumn';
import { Task } from '@/types/task';
import { statusLabels } from './TaskBadges';
import { useState } from 'react';
import { KanbanTaskCard } from './KanbanTaskCard';
import { createPortal } from 'react-dom';

interface TasksKanbanProps {
  tasks: Task[];
  onTaskStatusChange: (taskId: string, newStatus: Task['status']) => void;
  onTaskClick: (task: Task) => void;
}

const statuses: Task['status'][] = ['pending', 'in_progress', 'under_review', 'completed', 'cancelled'];

export const TasksKanban = ({ tasks, onTaskStatusChange, onTaskClick }: TasksKanbanProps) => {
    const [activeTask, setActiveTask] = useState<Task | null>(null);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      })
    );

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const task = tasks.find(t => t.id === active.id);
        if (task) {
            setActiveTask(task);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveTask(null);
        const { active, over } = event;
        if (!over) return;

        const activeId = String(active.id);
        const task = tasks.find((t) => t.id === activeId);

        if (!task) return;

        const overContainerId = over.data.current?.sortable?.containerId || over.id;
        
        if (statuses.includes(overContainerId as Task['status']) && task.status !== overContainerId) {
            onTaskStatusChange(task.id, overContainerId as Task['status']);
        }
    };

    const tasksByStatus = statuses.reduce((acc, status) => {
        acc[status] = tasks.filter(task => task.status === status);
        return acc;
    }, {} as Record<Task['status'], Task[]>);

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="flex gap-4 overflow-x-auto pb-4">
                {statuses.map(status => (
                    <KanbanColumn
                        key={status}
                        id={status}
                        title={statusLabels[status]}
                        tasks={tasksByStatus[status]}
                        onTaskClick={onTaskClick}
                    />
                ))}
            </div>
            {createPortal(
                <DragOverlay>
                    {activeTask ? <KanbanTaskCard task={activeTask} isOverlay onClick={() => {}} /> : null}
                </DragOverlay>,
                document.body
            )}
        </DndContext>
    );
};
